import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'
import { photoUrl } from '../utils/photoUrl'

const CLASS_COLORS = {
  acne: '#E11D48', post_acne_marks: '#EA580C', post_acne_scars: '#D97706', pores: '#0891B2', dark_circles: '#2563EB',
}
const CLASS_LABELS = {
  acne: 'Acne', post_acne_marks: 'Post-acne marks', post_acne_scars: 'Post-acne scars', pores: 'Pores', dark_circles: 'Dark circles',
}
// Fallbacks so a class always gets a colour + readable label, even if names drift.
const PALETTE = ['#E11D48', '#EA580C', '#D97706', '#0891B2', '#2563EB', '#16A34A', '#DB2777', '#7C3AED']
const colorFor = (c, i = 0) => CLASS_COLORS[c] || PALETTE[i % PALETTE.length]
const labelFor = c => CLASS_LABELS[c] || String(c || '').replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
const FONT = "'DM Sans',sans-serif"
const ctrlBtn = (active) => ({ background: active ? '#1E6B3C' : '#1a2a22', border: '1px solid #2a3a30', color: '#fff', borderRadius: 7, padding: '.35rem .65rem', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: FONT })

// Resize/compress a photo in the browser before upload (training doesn't need
// full-res, and it keeps each request well under the server's 10mb body limit).
function compressImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise(resolve => {
    let done = false
    const finish = v => { if (!done) { done = true; resolve(v) } }
    // Fallback: upload the raw file bytes as-is (used if the browser can't decode it).
    const raw = () => { const r = new FileReader(); r.onload = () => finish(r.result); r.onerror = () => finish(null); r.readAsDataURL(file) }
    // Safety net: some browsers fire NEITHER onload nor onerror for unsupported
    // formats (e.g. iPhone .HEIC), which would hang forever. Time out to raw.
    const timer = setTimeout(raw, 8000)
    const img = new Image()
    img.onload = () => {
      clearTimeout(timer)
      let { width, height } = img
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim }
        else { width = Math.round(width * maxDim / height); height = maxDim }
      }
      const c = document.createElement('canvas')
      c.width = width; c.height = height
      c.getContext('2d').drawImage(img, 0, 0, width, height)
      try { finish(c.toDataURL('image/jpeg', quality)) } catch { raw() }
      URL.revokeObjectURL(img.src)
    }
    img.onerror = () => { clearTimeout(timer); raw() }
    img.src = URL.createObjectURL(file)
  })
}

// Doctor/admin tool: draw boxes on skin photos to build a training dataset.
export default function LabelingTool() {
  const { user, fetchWithAuth } = useAuth()
  const navigate = useNavigate()
  const [images, setImages] = useState([])
  const [classes, setClasses] = useState(['acne', 'post_acne_marks', 'post_acne_scars', 'pores', 'dark_circles'])
  const [sel, setSel] = useState(null)          // selected image id
  const [boxes, setBoxes] = useState([])         // boxes for selected image
  const [cls, setCls] = useState('acne')         // active class for new boxes
  const [draft, setDraft] = useState(null)       // box being drawn
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [note, setNote] = useState('')
  const [recording, setRecording] = useState(false)
  const recRef = useRef(null)
  const noteBase = useRef('')
  const finalRef = useRef('')
  const imgWrapRef = useRef(null)
  const startRef = useRef(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [mode, setMode] = useState('draw')   // draw | pan
  const panStart = useRef(null)
  const pointers = useRef(new Map())   // active touch/pointer positions
  const pinch = useRef(null)           // two-finger gesture state
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }) }

  const load = useCallback(async () => {
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/labeling/images`)
      if (r.status === 403) { setMsg('Staff access required — log in as a doctor.'); return }
      const d = await r.json()
      setClasses(d.classes || classes)
      setImages(d.images || [])
      if (!sel && d.images?.length) selectImage(d.images[0])
    } catch { setMsg('Could not load images.') }
  }, [fetchWithAuth]) // eslint-disable-line

  useEffect(() => { load() }, [load])

  const selectImage = (im) => { setSel(im.id); setBoxes(im.boxes || []); setNote(im.note || ''); setZoom(1); setPan({ x: 0, y: 0 }); setMode('draw') }

  const current = images.find(i => i.id === sel)

  // ── Upload ──
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setBusy(true); setMsg(`Processing ${files.length}…`)
    try {
      const b64s = (await Promise.all(files.map(f => compressImage(f)))).filter(Boolean)
      if (!b64s.length) { setMsg('Could not read that photo. If it\'s an iPhone HEIC file, set Camera → Formats to "Most Compatible" (JPEG) and retry.'); return }
      let up = 0
      for (let i = 0; i < b64s.length; i += 3) {   // small chunks stay under the 10mb body limit
        setMsg(`Uploading ${Math.min(i + 3, b64s.length)}/${b64s.length}…`)
        const r = await fetchWithAuth(`${API_BASE}/api/labeling/upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ images: b64s.slice(i, i + 3) }),
        })
        if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || `Upload failed (HTTP ${r.status})`) }
        const d = await r.json(); up += d.uploaded || 0
      }
      setMsg(`Uploaded ${up} photo${up === 1 ? '' : 's'}.`)
      await load()
    } catch (err) { setMsg(err.message || 'Upload failed.') } finally { setBusy(false); e.target.value = '' }
  }

  // ── Box drawing (pointer = mouse + touch) ──
  const pct = (e) => {
    const rect = imgWrapRef.current.getBoundingClientRect()
    return {
      x: Math.min(100, Math.max(0, (e.clientX - rect.left) / rect.width * 100)),
      y: Math.min(100, Math.max(0, (e.clientY - rect.top) / rect.height * 100)),
    }
  }
  const twoDist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)
  const onWheel = (e) => setZoom(z => Math.min(6, Math.max(1, +(z - e.deltaY * 0.0015).toFixed(2))))
  const onDown = (e) => {
    if (!current) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2) {
      // two fingers → pinch zoom + pan; cancel any box being drawn
      const [a, b] = [...pointers.current.values()]
      pinch.current = { d0: twoDist(a, b), z0: zoom, pan0: { ...pan }, mid0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } }
      startRef.current = null; setDraft(null)
      return
    }
    if (pointers.current.size > 2) return
    if (mode === 'pan') { panStart.current = { px: e.clientX, py: e.clientY, ox: pan.x, oy: pan.y }; return }
    startRef.current = pct(e)
    setDraft({ ...startRef.current, w: 0, h: 0, cls })
  }
  const onMove = (e) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size >= 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()]
      const nz = Math.min(6, Math.max(1, +(pinch.current.z0 * (twoDist(a, b) / (pinch.current.d0 || 1))).toFixed(3)))
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      setZoom(nz)
      setPan({ x: pinch.current.pan0.x + (mid.x - pinch.current.mid0.x), y: pinch.current.pan0.y + (mid.y - pinch.current.mid0.y) })
      return
    }
    if (mode === 'pan') { if (panStart.current) { const s = panStart.current; setPan({ x: s.ox + (e.clientX - s.px), y: s.oy + (e.clientY - s.py) }) } return }
    if (!startRef.current) return
    const p = pct(e); const s = startRef.current
    setDraft({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y), cls })
  }
  const onUp = (e) => {
    pointers.current.delete(e?.pointerId)
    if (pointers.current.size < 2) pinch.current = null
    if (mode === 'pan') { panStart.current = null; return }
    if (pointers.current.size === 0) {
      if (draft && draft.w > 0.6 && draft.h > 0.6) setBoxes(prev => [...prev, draft])
      setDraft(null); startRef.current = null
    }
  }
  const removeBox = (i) => setBoxes(prev => prev.filter((_, j) => j !== i))

  // ── Save ──
  const save = async (goNext = false) => {
    if (!current) return
    setBusy(true)
    try {
      await fetchWithAuth(`${API_BASE}/api/labeling/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: current.id, boxes, note }),
      })
      setImages(prev => prev.map(im => im.id === current.id ? { ...im, boxes, note, status: boxes.length ? 'labeled' : 'pending' } : im))
      setMsg('Saved ✓')
      if (goNext) {
        const idx = images.findIndex(i => i.id === current.id)
        const next = images[idx + 1]
        if (next) selectImage(next)
      }
    } catch { setMsg('Save failed.') } finally { setBusy(false) }
  }

  const delImage = async () => {
    if (!current || !window.confirm('Delete this image and its labels?')) return
    setBusy(true)
    try {
      await fetchWithAuth(`${API_BASE}/api/labeling/image/${current.id}`, { method: 'DELETE' })
      const remaining = images.filter(i => i.id !== current.id)
      setImages(remaining); setSel(remaining[0]?.id || null); setBoxes(remaining[0]?.boxes || [])
    } catch {} finally { setBusy(false) }
  }

  const exportData = async () => {
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/labeling/export`)
      const d = await r.json()
      setMsg(`${d.count} labeled images ready. Run ml/pull_from_app.py to build the YOLO dataset, then ml/train.py.`)
    } catch { setMsg('Export failed.') }
  }

  // Speak a note → browser live speech-to-text fills the box (editable)
  const startRec = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) { setMsg('Voice input not supported in this browser — please type the note.'); return }
    noteBase.current = note ? note.trim() + ' ' : ''
    finalRef.current = ''
    const rec = new SR()
    rec.lang = 'en-IN'; rec.continuous = true; rec.interimResults = true
    rec.onresult = (e) => {
      let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalRef.current += e.results[i][0].transcript + ' '
        else interim += e.results[i][0].transcript
      }
      setNote(noteBase.current + finalRef.current + interim)
    }
    rec.onerror = (ev) => { setRecording(false); setMsg(ev.error === 'not-allowed' ? 'Microphone blocked — allow mic access or type.' : 'Voice error — please type the note.') }
    rec.onend = () => setRecording(false)
    try { rec.start(); recRef.current = rec; setRecording(true); setMsg('Listening… tap Stop when done.') }
    catch { setMsg('Could not start voice — please type the note.') }
  }
  const stopRec = () => { try { recRef.current?.stop() } catch {}; setRecording(false) }

  const labeledCount = images.filter(i => i.status === 'labeled').length

  if (user && user.role !== 'doctor' && user.role !== 'admin') {
    return <div style={{ padding: '2rem', fontFamily: FONT }}>This tool is for clinic staff only.</div>
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0F1713', color: '#fff', fontFamily: FONT }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '.9rem 1.2rem', borderBottom: '1px solid #223', background: '#12201A' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#9fe', fontSize: '1.1rem', cursor: 'pointer' }}>‹ Back</button>
        <strong style={{ fontSize: '1rem' }}>Skin Labeling — Training Data</strong>
        <span style={{ fontSize: '.72rem', color: '#8fb' }}>{labeledCount}/{images.length} labeled</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <label style={{ background: '#1E6B3C', padding: '.45rem .9rem', borderRadius: 8, fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}>
            + Upload photos
            <input type="file" accept="image/*" multiple onChange={onUpload} style={{ display: 'none' }} />
          </label>
          <button onClick={exportData} style={{ background: '#235233', color: '#fff', border: 'none', padding: '.45rem .9rem', borderRadius: 8, fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}>Export</button>
        </div>
      </div>

      {msg && <div style={{ padding: '.5rem 1.2rem', fontSize: '.78rem', color: '#8fe', background: '#12201A' }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 56px)' }}>
        {/* Thumbnail list */}
        <div style={{ width: 120, overflowY: 'auto', borderRight: '1px solid #223', padding: 8, flexShrink: 0 }}>
          {images.length === 0 && <div style={{ fontSize: '.7rem', color: '#7a8', padding: 8 }}>Upload photos to start.</div>}
          {images.map(im => (
            <button key={im.id} onClick={() => selectImage(im)}
              style={{ display: 'block', width: '100%', marginBottom: 6, padding: 0, border: sel === im.id ? '2px solid #5AE' : '2px solid transparent', borderRadius: 6, overflow: 'hidden', cursor: 'pointer', position: 'relative', background: '#000' }}>
              <img src={photoUrl(im.url)} alt="" style={{ width: '100%', display: 'block', opacity: sel === im.id ? 1 : .7 }} />
              {im.status === 'labeled' && <span style={{ position: 'absolute', top: 2, right: 2, background: '#16a34a', color: '#fff', fontSize: '.55rem', padding: '1px 4px', borderRadius: 4 }}>✓</span>}
            </button>
          ))}
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, overflow: 'auto' }}>
          {current ? (
            <div style={{ position: 'relative', maxWidth: 640, width: '100%' }}>
              <div onWheel={onWheel} style={{ position: 'relative', width: '100%', overflow: 'hidden', borderRadius: 8, background: '#000', touchAction: 'none' }}>
                <div ref={imgWrapRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
                  style={{ position: 'relative', transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', cursor: mode === 'pan' ? 'grab' : 'crosshair', userSelect: 'none', touchAction: 'none' }}>
                  <img src={photoUrl(current.url)} alt="" draggable={false} style={{ width: '100%', display: 'block' }} />
                  {boxes.map((b, i) => (
                    <div key={i} onClick={(e) => { e.stopPropagation(); if (mode !== 'pan') removeBox(i) }} title="Click to remove"
                      style={{ position: 'absolute', left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%`, border: `${2 / zoom}px solid ${colorFor(b.cls, classes.indexOf(b.cls))}`, background: `${colorFor(b.cls, classes.indexOf(b.cls))}22`, cursor: 'pointer' }}>
                      <span style={{ position: 'absolute', top: `${-16 / zoom}px`, left: 0, fontSize: `${0.6 / zoom}rem`, fontWeight: 700, background: colorFor(b.cls, classes.indexOf(b.cls)), color: '#fff', padding: `0 ${4 / zoom}px`, borderRadius: 3, whiteSpace: 'nowrap' }}>{labelFor(b.cls)}</span>
                    </div>
                  ))}
                  {draft && <div style={{ position: 'absolute', left: `${draft.x}%`, top: `${draft.y}%`, width: `${draft.w}%`, height: `${draft.h}%`, border: `${2 / zoom}px dashed ${colorFor(cls, classes.indexOf(cls))}`, background: `${colorFor(cls, classes.indexOf(cls))}22`, pointerEvents: 'none' }} />}
                </div>
              </div>
              {/* Zoom + pan controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => setMode(m => m === 'pan' ? 'draw' : 'pan')} style={ctrlBtn(mode === 'pan')}>{mode === 'pan' ? '✋ Pan' : '✏️ Draw'}</button>
                <button onClick={() => setZoom(z => Math.max(1, +(z - 0.5).toFixed(2)))} style={ctrlBtn()}>−</button>
                <span style={{ fontSize: '.72rem', color: '#9fe', minWidth: 42, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom(z => Math.min(6, +(z + 0.5).toFixed(2)))} style={ctrlBtn()}>+</button>
                <button onClick={resetView} style={ctrlBtn()}>Reset</button>
              </div>
              <div style={{ fontSize: '.6rem', color: '#7a8', textAlign: 'center', marginTop: 4 }}>Zoom in for small spots (+ / − or scroll). Switch to ✋ Pan to move around, back to ✏️ Draw to box.</div>
            </div>
          ) : <div style={{ color: '#7a8' }}>Select or upload a photo.</div>}
        </div>

        {/* Controls */}
        <div style={{ width: 190, borderLeft: '1px solid #223', padding: 14, flexShrink: 0, height: '100%', overflowY: 'auto' }}>
          <div style={{ fontSize: '.68rem', color: '#8fb', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Draw as</div>
          {classes.map((c, ci) => (
            <button key={c} onClick={() => setCls(c)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', marginBottom: 6, padding: '.5rem .6rem', borderRadius: 8, border: cls === c ? `2px solid ${colorFor(c, ci)}` : '2px solid #2a3a30', background: cls === c ? `${colorFor(c, ci)}22` : 'transparent', color: '#fff', cursor: 'pointer', fontSize: '.8rem', fontWeight: 600, fontFamily: FONT }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: colorFor(c, ci) }} />
              {labelFor(c)}
            </button>
          ))}
          <div style={{ fontSize: '.62rem', color: '#7a8', margin: '10px 0', lineHeight: 1.5 }}>
            Drag on the photo to draw a box. Click a box to delete it.
          </div>
          <div style={{ fontSize: '.72rem', color: '#9fe', marginBottom: 6 }}>{boxes.length} box{boxes.length === 1 ? '' : 'es'} on this photo</div>
          <button disabled={!boxes.length} onClick={() => setBoxes(prev => prev.slice(0, -1))}
            style={{ width: '100%', marginBottom: 10, padding: '.45rem', borderRadius: 8, border: '1px solid #2a3a30', background: boxes.length ? '#1a2a22' : 'transparent', color: boxes.length ? '#fff' : '#556', fontWeight: 600, fontSize: '.74rem', cursor: boxes.length ? 'pointer' : 'default', fontFamily: FONT }}>
            ↶ Undo last box
          </button>

          {/* Doctor's note — type or speak */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: '.64rem', color: '#8fb', textTransform: 'uppercase', letterSpacing: '.05em' }}>Doctor's note</span>
              <button disabled={!current} onClick={recording ? stopRec : startRec}
                style={{ ...ctrlBtn(recording), marginLeft: 'auto', padding: '.25rem .55rem', fontSize: '.68rem' }}>
                {recording ? '⏹ Stop' : '🎤 Speak'}
              </button>
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} disabled={!current}
              placeholder="Type, or tap 🎤 to explain / suggest…"
              style={{ width: '100%', minHeight: 58, resize: 'vertical', background: '#12201a', color: '#fff', border: '1px solid #2a3a30', borderRadius: 8, padding: '.5rem', fontSize: '.74rem', fontFamily: FONT, boxSizing: 'border-box' }} />
          </div>

          <div style={{ position: 'sticky', bottom: -14, background: '#0F1713', paddingTop: 8, marginTop: 4 }}>
            <button disabled={busy || !current} onClick={() => save(false)} style={{ width: '100%', marginBottom: 6, padding: '.6rem', borderRadius: 8, border: 'none', background: '#1E6B3C', color: '#fff', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', fontFamily: FONT }}>Save</button>
            <button disabled={busy || !current} onClick={() => save(true)} style={{ width: '100%', marginBottom: 6, padding: '.6rem', borderRadius: 8, border: '1.5px solid #35A05C', background: 'transparent', color: '#8fe', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', fontFamily: FONT }}>Save & next →</button>
            <button disabled={busy || !current} onClick={delImage} style={{ width: '100%', padding: '.5rem', borderRadius: 8, border: '1px solid #612', background: 'transparent', color: '#f88', fontWeight: 600, fontSize: '.74rem', cursor: 'pointer', fontFamily: FONT }}>Delete photo</button>
          </div>
        </div>
      </div>
    </div>
  )
}
